/**
 * Base types that every per-endpoint export in {@link ./endpoint-contracts.ts}
 * conforms to. These live in a separate file so {@link ./endpoint-contracts.ts}
 * can be empty until Phase C migrations start registering real endpoints.
 *
 * See docs/specs/endpoint-contracts/architecture.md §4.5–§4.6 and
 * docs/specs/unified-search-and-filter/architecture.md §5.6.
 */

/** Value-kind discriminant for a filter parameter (mirrors backend FilterKind). */
export type EndpointContractFilterKind =
    | 'string'
    | 'uuid'
    | 'enum'
    | 'csv'
    | 'date'
    | 'datetime'
    | 'boolean'
    | 'number'

/**
 * Reference-entity lookup descriptor for `kind: 'uuid'` (single-select) and
 * `kind: 'csv', element: 'uuid'` (multi-select) filters. When present on a
 * descriptor, {@link FilterPanel} renders a generic Combobox that drives a
 * type-ahead search against the lookup endpoint.
 *
 * **Phase 525 transition shape.** The forward shape is `{endpoint, searchParam,
 * entityType}` pointing at the entity's `/links` projection endpoint that
 * returns thin {@link EntityLink} rows `{id, displayName, entityType, subType}`
 * — no per-page picker wiring and no separate `service.detail()` call required.
 * The legacy {@link labelField} / {@link descriptionField} fields remain on the
 * type while contracts migrate to the new shape; once every contract carries
 * `entityType`, the legacy fields are dropped from the type entirely (Phase 10
 * cleanup of issue 525).
 */
export interface LookupDescriptor {
    /**
     * `ApiEndpoint` enum entry name. New shape points at the entity's `/links`
     * projection (e.g. `'apiInternalIdentitiesLinks'`). Legacy shape points at
     * the entity's main list endpoint (e.g. `'apiInternalIdentities'`); the
     * legacy path remains until Phase 10 migrates every contract.
     */
    readonly endpoint: string
    /**
     * Query parameter the entity's lookup endpoint accepts for type-ahead
     * search (always `'q'` — matches the backend's `@Searchable` slot).
     */
    readonly searchParam: string
    /**
     * Bounded entity type the lookup targets. Mirrors the
     * {@code com.irian.iam.shared.api.EntityType} value the backend returns
     * on every {@link EntityLink} row. New shape only — legacy contracts
     * omit it until Phase 10 migrates them.
     */
    readonly entityType?:
        | 'IDENTITY'
        | 'PARTY'
        | 'RESOURCE'
        | 'SYSTEM'
        | 'ENFORCEMENT_OBJECT'
        | 'ACCESS_PROFILE'
        | 'FUNCTION_GROUP'
        | 'AUTH_USER'
        | 'REPORT_CATEGORY'
    /**
     * **Legacy 429 shape — to be removed in Phase 10 of issue 525.** Field
     * on the (non-`/links`) response DTO rendered as the option's primary
     * line. Replaced by the `displayName` slot on {@link EntityLink} rows.
     */
    readonly labelField?: string
    /**
     * **Legacy 429 shape — to be removed in Phase 10 of issue 525.** Optional
     * secondary line below the label.
     */
    readonly descriptionField?: string
}

/**
 * Common fields every filter descriptor carries. The discriminated union
 * below splits the `kind` axis so that `lookup` can only be declared on
 * the two shapes that support it (`'uuid'` single-select and
 * `'csv' + element: 'uuid'` multi-select).
 */
interface FilterDescriptorBase {
    readonly name: string
    /**
     * Human-readable label used as the chip prefix and panel section heading.
     * **Required** — every contract descriptor must declare one. The backend
     * codegen populates it from an i18n lookup during Phase C; in tests it is
     * inlined on the mock contract. When the field is missing at runtime, the
     * panel throws a clear error rather than silently fabricating a label
     * from the param name (HQG round: "no silent fallbacks — break if
     * something is wrong").
     */
    readonly displayName: string
    /**
     * For `uuid` filters — the API path that returns entity options for the
     * picker combobox. When the endpoint returns 403 the picker degrades to a
     * plain UUID text input (SEC-06). Predates {@link LookupDescriptor};
     * the new richer `lookup` shape supersedes this thin field but both
     * coexist while the codebase migrates.
     */
    readonly referenceEndpoint?: string
    /**
     * For `uuid` filters and dynamic attributes whose value type resolves to a
     * foreign entity — route pattern for the entity's detail page, using the
     * literal `:id` placeholder for the UUID (e.g., `/identity-management/
     * parties/:id`). When the filter has a value, the chip renders its label
     * as an {@link EntityLink} pointing at this route. Absent → chip renders
     * as plain text.
     */
    readonly detailRoute?: string
    /**
     * When true, the filter exposes a "Not set" option that writes the
     * {@link IS_NULL_SENTINEL} to the URL (§4.3 nullable-filter rule).
     */
    readonly nullable?: boolean
    /**
     * When set, the filter is part of a grouped from/to date-range pair. Two
     * descriptors that share the same {@link dateRange} group key render as a
     * single from/to control pair (§4.3 dateRange grouping).
     */
    readonly dateRange?: string
    /** Marks the bound of a date-range pair — `from` or `to`. */
    readonly dateRangeBound?: 'from' | 'to'
    /**
     * When true, the filter is only visible to admin users and is excluded
     * from the panel for non-admins (SEC-05 / §4.3 admin-only filters).
     */
    readonly adminOnly?: boolean
    /**
     * Type-table name — a key in `AppConfig.typeValuesByName` — used to
     * resolve raw enum codes to localized labels for display. When the
     * filter kind is `enum` or `csv<enum>`, the UnifiedSearchBar maps each
     * value through `labelOf(typeValuesByName[typeTable], code)` so the UI
     * shows "Person" instead of the raw `PERSON` code. The URL and the
     * option `value` attribute always remain the raw code.
     *
     * Backend-populated (planned in 342.03 codegen). When absent, the bar
     * falls back to the raw code — safe for all call sites but intended
     * only as a transitional state.
     */
    readonly typeTable?: string
    /**
     * For `boolean` filters — the per-field user-facing labels for the two
     * states. When set, the chip renders the selected label alone (e.g.,
     * "Active" / "Inactive") instead of `{displayName}: true` / `{displayName}:
     * false`, which reads as engineering-speak. The panel toggle uses the
     * same labels.
     *
     * When absent, the chip falls back to `{displayName}: Yes` / `{displayName}:
     * No` using the shared `common.yes` / `common.no` i18n keys. Every Boolean
     * filter that ships should carry explicit labels — the fallback is an
     * escape hatch.
     */
    readonly booleanLabels?: {
        readonly true: string
        readonly false: string
    }
}

/**
 * Descriptor for a single filter parameter on an endpoint contract. The
 * optional fields starting with `displayName` were added by the Unified Search
 * spec §5.6 — they are consumed by {@link UnifiedSearchBar} but older contracts
 * without them continue to work for legacy `SearchInput`-based pages.
 *
 * The `kind` axis is type-narrowed: `lookup` is only legal on `kind: 'uuid'`
 * (single-select) and `kind: 'csv', element: 'uuid'` (multi-select). All
 * other kinds reject the property at compile time. See {@link LookupDescriptor}
 * for the rendered behaviour.
 */
export type EndpointContractFilterDescriptor =
    | (FilterDescriptorBase & {
        readonly kind: 'uuid'
        readonly lookup?: LookupDescriptor
        readonly element?: never
        readonly values?: never
    })
    | (FilterDescriptorBase & {
        readonly kind: 'csv'
        readonly element: 'uuid'
        readonly lookup?: LookupDescriptor
        readonly values?: never
    })
    | (FilterDescriptorBase & {
        readonly kind: 'csv'
        readonly element: 'enum' | 'string'
        readonly values?: ReadonlyArray<string>
        readonly lookup?: never
    })
    | (FilterDescriptorBase & {
        readonly kind: 'enum'
        readonly values?: ReadonlyArray<string>
        readonly element?: never
        readonly lookup?: never
    })
    | (FilterDescriptorBase & {
        readonly kind: 'string' | 'date' | 'datetime' | 'boolean' | 'number'
        readonly values?: never
        readonly element?: never
        readonly lookup?: never
    })

/**
 * Flattened structural view of {@link EndpointContractFilterDescriptor} used
 * by {@link FilterPanel} internals — panel code reads `descriptor.values` /
 * `descriptor.element` / `descriptor.lookup` regardless of the kind branch.
 * Authors should always declare via {@link EndpointContractFilterDescriptor}
 * so the discriminated union enforces the legal combinations; the panel
 * narrows back to this flat shape for rendering.
 */
export interface FilterDescriptorView extends FilterDescriptorBase {
    readonly kind: EndpointContractFilterKind
    readonly values?: ReadonlyArray<string>
    readonly element?: EndpointContractFilterKind
    readonly lookup?: LookupDescriptor
}

/**
 * Shape every search/filter contract conforms to — the structural surface
 * {@link UnifiedSearchBar} reads to render its panel and chips.
 *
 * The `sort` map is a `Record` of identifier → dotted JPA path string. The
 * `filter` map is keyed by an upper-case filter identifier; each entry carries
 * the `name` the backend expects as a query-string parameter plus the value
 * `kind` that narrows the accepted TypeScript value type.
 *
 * This shape carries no binding to an HTTP endpoint — pages that narrow an
 * already-loaded array client-side declare a `SearchAndFilterContract` literal
 * and feed the bar without ever calling {@link useServerPagination}.
 * Server-paginated callers declare an {@link EndpointContract} (a strict
 * subtype) so {@code RequireEndpointContractTypescriptMatch} keeps the TS
 * shape pinned to the backend's `@SortableFields` declaration.
 */
export interface SearchAndFilterContract {
    readonly sort: Readonly<Record<string, string>>
    readonly filter: Readonly<Record<string, EndpointContractFilterDescriptor>>
    /**
     * Optional search slot — when present, the bar renders a free-text input
     * that writes to the given param (default `q`). Matches the backend's
     * `@Searchable` field scanning. See architecture.md §5.6.
     */
    readonly search?: {
        readonly param: string
        readonly placeholder?: string
    }
    /**
     * Optional namespace prefix for URL params — allows multiple bars on the
     * same page to operate on independent state (§5.7).
     */
    readonly namespace?: string
}

/**
 * Shape every `{Controller}{Method}Endpoint` export conforms to. Currently a
 * structural alias for {@link SearchAndFilterContract} — the distinct name
 * signals that the contract is bound to a real backend endpoint and is
 * therefore subject to {@code RequireEndpointContractTypescriptMatch}, which
 * validates the export against the controller's `@SortableFields`
 * declaration. The convention check parses {@code endpoint-contracts.ts}
 * by export name (`{Controller}{Method}Endpoint`), not by type name, so the
 * alias is purely a documentation marker today.
 */
export type EndpointContract = SearchAndFilterContract

/**
 * Return a new {@link SearchAndFilterContract} with every filter whose
 * descriptor `name` matches an entry in {@code paramNamesToOmit} removed.
 * Used by promoted-type list pages (issue 301) so a locked filter (e.g.
 * `partyType=TEAM` on the by-type route) never surfaces in the filter
 * panel or chip rail.
 */
export function omitContractFilters<T extends SearchAndFilterContract>(
    contract: T,
    paramNamesToOmit: readonly string[],
): SearchAndFilterContract {
    if (paramNamesToOmit.length === 0) return contract
    const omitSet = new Set(paramNamesToOmit)
    const filter: Record<string, EndpointContractFilterDescriptor> = {}
    for (const [key, descriptor] of Object.entries(contract.filter)) {
        if (!omitSet.has(descriptor.name)) {
            filter[key] = descriptor
        }
    }
    return {...contract, filter}
}

/**
 * Sentinel written to the URL when the user selects "Not set" on a nullable
 * filter. The backend decodes this in `FilterParamInterceptor` as an IS NULL
 * predicate (§4.3 nullable filters).
 */
export const IS_NULL_SENTINEL = '__null__'
